// 代理商后台路由 —— 正式前缀 /agent/*（PRD C1.2）
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { ToastProvider } from '@/components/Toast';
import ErrorBoundary from '@/components/ErrorBoundary';
import OfflineBanner from '@/components/OfflineBanner';
import { setAuthFailureHandler } from '@/api/client';
import RequireAuth from '@/auth/RequireAuth';
import RequireSetup from '@/auth/RequireSetup';
import Layout from '@/components/Layout';
import LoginPage from '@/pages/auth';

const VerifyEmailPage = lazy(() => import('@/pages/auth/VerifyEmailPage'));
const PortalLoginPage = lazy(() => import('@/pages/portal-login'));
const DashboardPage = lazy(() => import('@/pages/dashboard'));
const MerchantsPage = lazy(() => import('@/pages/merchants'));
const FundsPage = lazy(() => import('@/pages/settlement/funds'));
const RisksPage = lazy(() => import('@/pages/finance/risks'));
const ReportsPage = lazy(() => import('@/pages/reports'));
const SignWitnessPage = lazy(() => import('@/pages/biz/sign-witness'));
const AuthorizedPricesPage = lazy(() => import('@/pages/biz/authorized-prices'));
const RateCalculatorPage = lazy(() => import('@/pages/biz/rate-calculator'));
// 第一期不做：展业物料（PAGE-AGT-BIZ-008）
// const MaterialsPage = lazy(() => import('@/pages/biz/materials'));
const SettlementConfigPage = lazy(() => import('@/pages/settlement/settlement-config'));
const ReconciliationPage = lazy(() => import('@/pages/settlement/reconciliation'));
const ReversalsPage = lazy(() => import('@/pages/settlement/reversals'));
// P2 菜单合并壳（纯壳 ?tab= 持态 + lazy 挂载既有页面组件，业务页面不再直挂路由）
const SpreadCenterPage = lazy(() => import('@/pages/settlement/spread'));
const ProfileOnboardingShell = lazy(() => import('@/pages/settings/profile/shell'));
const SecurityPreferencesShell = lazy(() => import('@/pages/settings/security/shell'));
const PayoutAccountsShell = lazy(() => import('@/pages/settings/payouts/shell'));

function PageFallback() {
  return (
    <div className="h-full flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-brand-600 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-slate-500 mt-3">加载页面…</p>
      </div>
    </div>
  );
}

/** 旧无前缀书签 → 正式 /agent/*（P2 起合并壳路径带 ?tab= 深链） */
const LEGACY: { from: string; to: string }[] = [
  { from: 'dashboard', to: '/agent/dashboard' },
  { from: 'merchants', to: '/agent/biz/merchants' },
  { from: 'fund', to: '/agent/biz/merchants' },
  { from: 'finance/commissions', to: '/agent/settlement/spread?tab=overview' },
  { from: 'finance/settlements', to: '/agent/settlement/spread?tab=statements' },
  { from: 'finance/risks', to: '/agent/settlement/notices' },
  { from: 'reports', to: '/agent/reports' },
  { from: 'settings/profile', to: '/agent/settings/profile?tab=profile' },
  { from: 'settings/security', to: '/agent/settings/security?tab=security' },
  { from: 'settings/payouts', to: '/agent/settings/payouts?tab=settlement' },
  { from: 'settings/receive-accounts', to: '/agent/settings/payouts?tab=distribute' },
];

/** 路由内部：注册 401 鉴权失败回调 → navigate('/login') */
function AppRoutes() {
  const navigate = useNavigate();
  useEffect(() => {
    setAuthFailureHandler(() => navigate('/login'));
    return () => setAuthFailureHandler(null);
  }, [navigate]);

  return (
    <Routes>
      {/* 公开路由 */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      {/* 运营代登录落地页：无 JWT，必须在 RequireAuth 之外 */}
      <Route path="/portal-login" element={<PortalLoginPage />} />
      <Route path="/" element={<Navigate to="/agent/dashboard" replace />} />

      {/* 受保护路由（RequireAuth：无 token → /login；RequireSetup：强制改密/绑 2FA） */}
      <Route element={<RequireAuth />}>
        <Route element={<RequireSetup />}>
        <Route path="/agent" element={<Layout />}>
          <Route index element={<Navigate to="dashboard" replace />} />

          <Route path="dashboard" element={<DashboardPage />} />

          {/* 展业中心 */}
          <Route path="biz/merchants" element={<MerchantsPage />} />
          <Route path="biz/merchants/new" element={<MerchantsPage />} />
          <Route path="biz/sign-witness" element={<SignWitnessPage />} />
          <Route path="biz/authorized-prices" element={<AuthorizedPricesPage />} />
          <Route path="biz/rate-calculator" element={<RateCalculatorPage />} />
          {/* 第一期不做：展业物料（PAGE-AGT-BIZ-008） */}
          {/* <Route path="biz/materials" element={<MaterialsPage />} /> */}

          {/* 结算中心 · 价差模型（P2：价差收益合并壳，旧路径 302 到 ?tab= 深链） */}
          <Route path="settlement/spread" element={<SpreadCenterPage />} />
          <Route
            path="settlement/spread-overview"
            element={<Navigate to="/agent/settlement/spread?tab=overview" replace />}
          />
          <Route
            path="settlement/spread-summary"
            element={<Navigate to="/agent/settlement/spread?tab=overview" replace />}
          />
          <Route
            path="settlement/spread-statements"
            element={<Navigate to="/agent/settlement/spread?tab=statements" replace />}
          />
          <Route
            path="settlement/withdrawals"
            element={<Navigate to="/agent/settlement/spread?tab=withdrawals" replace />}
          />
          <Route path="settlement/funds" element={<FundsPage />} />
          <Route path="settlement/notices" element={<RisksPage />} />
          <Route path="settlement/reports" element={<ReportsPage />} />
          <Route path="settlement/settlement-config" element={<SettlementConfigPage />} />
          <Route path="settlement/reconciliation" element={<ReconciliationPage />} />
          <Route path="settlement/reversals" element={<ReversalsPage />} />

          <Route path="reports" element={<ReportsPage />} />

          {/* 个人中心（P2：三合并壳，旧路径 302 到 ?tab= 深链） */}
          <Route path="settings/profile" element={<ProfileOnboardingShell />} />
          {/* 我的开户资料旧路由（2026-09-19-N1 建号极简 · PAGE-AGT-PRF-006）→ 资质与开户壳 */}
          <Route
            path="profile/onboarding"
            element={<Navigate to="/agent/settings/profile?tab=onboarding" replace />}
          />
          <Route path="settings/security" element={<SecurityPreferencesShell />} />
          <Route
            path="settings/preferences"
            element={<Navigate to="/agent/settings/security?tab=preferences" replace />}
          />
          <Route path="settings/payouts" element={<PayoutAccountsShell />} />
          <Route
            path="settings/receive-accounts"
            element={<Navigate to="/agent/settings/payouts?tab=distribute" replace />}
          />

          <Route path="*" element={<Navigate to="/agent/dashboard" replace />} />
        </Route>
        </Route>
      </Route>

      {/* 旧路径兼容 */}
      {LEGACY.map(({ from, to }) => (
        <Route key={from} path={from} element={<Navigate to={to} replace />} />
      ))}
      <Route path="*" element={<Navigate to="/agent/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <OfflineBanner />
        <BrowserRouter>
          <Suspense fallback={<PageFallback />}>
            <AppRoutes />
          </Suspense>
        </BrowserRouter>
      </ToastProvider>
    </ErrorBoundary>
  );
}
